import {Component, OnInit, Optional} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {FUISpinnersService, FUIStackedPageContextService, FUIStackedPageService} from "@fedelta-pos/ui";
import {ErrorHandlerService} from "../../../error-handler.service";
import {CustomerInstanceData, CustomerInstanceFeaturesData} from "../customer-instance.data";
import {CustomerInstanceService} from "../customer-instance.service";
import {CustomerInstanceVenueData} from "../../customer-instance-venue/customer-instance-venue.data";
import {
    CustomerInstanceVenueSearchPageComponent
} from "../../customer-instance-venue/customer-instance-venue-search/customer-instance-venue-search-page.component";
import {CustomerInstanceVenueService} from "../../customer-instance-venue/customer-instance-venue.service";
import {BehaviorSubject} from "rxjs";


@Component({
    selector: "fhm-customer-instance-edit-page",
    styleUrls: ["customer-instance-edit-page.component.scss"],
    templateUrl: "./customer-instance-edit-page.component.html",
    providers: [CustomerInstanceService, CustomerInstanceVenueService],
    standalone: false
})
export class CustomerInstanceEditPageComponent implements OnInit {

    id: string;
    data: CustomerInstanceData = new CustomerInstanceData();
    oldData: CustomerInstanceData;

    venues: CustomerInstanceVenueData[] = [];

    newCustomerInstance: boolean;

    formGroup: FormGroup;

    editingInstance: boolean;
    editingFeatures: boolean;

    featuresData: CustomerInstanceFeaturesData;
    oldFeaturesData: CustomerInstanceFeaturesData;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private errorHandler: ErrorHandlerService,
        private spinnersService: FUISpinnersService,
        private formBuilder: FormBuilder,
        private customerInstanceService: CustomerInstanceService,
        private customerInstanceVenueService: CustomerInstanceVenueService,
        private stackedPageService: FUIStackedPageService,
        @Optional() private stackedPageContextService: FUIStackedPageContextService) {

        this.formGroup = this.formBuilder.group({
            id: ["", [Validators.required, Validators.maxLength(35)]],
            brand: ["", [Validators.maxLength(35)]]
        });

        this.formGroup.get("id")["required$"] = new BehaviorSubject(true);
    }

    ngOnInit(): void {
        if (this.stackedPageContextService) {
            this.stackedPageContextService.title = "Customer Instance";
        }

        this.route.paramMap.subscribe(async (params) => {
            this.id = params.get("id");
            this.init();
        });
    }

    async init(): Promise<void> {
        if (this.id === "new" || this.stackedPageContextService) {
            this.data = new CustomerInstanceData();
            this.newCustomerInstance = true;
        } else {
            this.spinnersService.open();
            this.newCustomerInstance = false;
            try {
                this.data = await this.customerInstanceService.getCustomerInstance(this.id);
                this.venues = await this.customerInstanceService.getCustomerInstanceVenues(this.id);
                this.featuresData = await this.customerInstanceService.getCustomerInstanceFeatures(this.id);
            } catch (error) {
                this.errorHandler.handleErrorResponse(error);
            } finally {
                this.spinnersService.close();
            }
        }
    }

    get title(): string {
        if (this.newCustomerInstance) {
            return "New Customer Instance";
        }

        if (!this.data) {
            return "";
        }

        if (!this.data.id) {
            return "New Customer Instance";
        }

        return this.data.clientId;
    }

    done(): void {
        if (this.stackedPageContextService) {
            this.stackedPageContextService.cancel();
        } else {
            if (this.data && this.data.deploymentType === CustomerInstanceData.DEPLOYMENT_STATUS_CHEWZIE) {
                this.router.navigate(["/main/customer-instance-search/chewzie"]);
            } else {
                this.router.navigate(["/main/customer-instance-search/fedelta"]);
            }
        }
    }

    async save(): Promise<void> {
        Object.keys((this.formGroup.controls)).forEach((key) => {
            this.formGroup.controls[key].markAsTouched();
            this.formGroup.controls[key].updateValueAndValidity();
        });
        if (this.formGroup.valid) {
            this.spinnersService.open();
            try {
                this.data = await this.customerInstanceService.createCustomerInstance(this.data);
                this.editingInstance = false;
                this.id = this.data.id;
                if (this.stackedPageContextService) {
                    this.stackedPageContextService.confirm(this.id);
                } else {
                    await this.router.navigate(["/main/customer-instance-edit/" + this.data.id]);
                    this.init();
                }
            } catch (error) {
                this.errorHandler.handleErrorResponse(error);
            } finally {
                this.spinnersService.close();
            }
        }
    }


    addNewVenue(): void {
        this.router.navigate(["/main/customer-instance-venue-edit/new"],
            {queryParams: {instanceId: this.id, clientId: this.data.clientId}});
    }

    addExistingVenue(): void {
        this.stackedPageService.open(CustomerInstanceVenueSearchPageComponent, {searchMode: true}).toPromise().then(async (data) => {
            if (data) {
                try {
                    await this.customerInstanceVenueService.assignInstanceToCustomerInstanceVenue(data, this.id);
                } catch (e) {
                    this.errorHandler.handleErrorResponse(e);
                }
                this.venues = await this.customerInstanceService.getCustomerInstanceVenues(this.id);
            }
        });
    }

    editVenue(customerInstanceVenue: CustomerInstanceVenueData): void {
        this.router.navigate(["/main/customer-instance-venue-edit/" + customerInstanceVenue.id]);
    }

    edit(): void {
        this.oldData = Object.assign({}, this.data);
        this.editingInstance = true;
    }

    cancelEdit(): void {
        this.data = this.oldData;
        this.editingInstance = false;
    }

    editFeatures(): void {
        this.oldFeaturesData = Object.assign({}, this.featuresData);
        this.editingFeatures = true;
    }

    cancelEditFeatures(): void {
        this.featuresData = this.oldFeaturesData;
        this.editingFeatures = false;
    }

    async update(): Promise<void> {
        Object.keys((this.formGroup.controls)).forEach((key) => {
            this.formGroup.controls[key].markAsTouched();
            this.formGroup.controls[key].updateValueAndValidity();
        });
        if (this.formGroup.valid) {
            this.spinnersService.open();
            try {
                await this.customerInstanceService.updateCustomerInstance(this.data.id, this.data.clientId, this.data.instanceGroupId, this.data.brand);
                this.editingInstance = false;
                this.id = this.data.id;
                await this.router.navigate(["/main/customer-instance-edit/" + this.data.id]);
                this.init();
            } catch (error) {
                this.errorHandler.handleErrorResponse(error);
            } finally {
                this.spinnersService.close();
            }
        }
    }

    async updateFeatures(): Promise<void> {
        this.spinnersService.open();
        try {
            await this.customerInstanceService.saveCustomerInstanceFeatures(this.featuresData);
            this.editingFeatures = false;
        } catch (error) {
            this.errorHandler.handleErrorResponse(error);
        } finally {
            this.spinnersService.close();
        }
    }
}
